import { useContext, memo, useRef, useEffect, useState, useMemo } from 'react'
import { LuSearch } from 'react-icons/lu';
import { FilterOutlined, PlusOutlined, MinusOutlined, AppstoreOutlined } from '@ant-design/icons';
import { Button, Input, Empty, Typography, Spin, Popover, Select, Divider, Badge } from 'antd';
import { FixedSizeList as List } from 'react-window';
import { campaignCreationContext } from '@ts/constants';
import { IClusterFormData, IProductGroup, BCC_MODAL_TYPES } from '@/ts/common-types';
import { capitalizeFirst, getCategoryOptions, startCase } from '@ts/utils';

const { Text } = Typography;

enum LEFT_PANEL_TABS {
    CLUSTERS = "clusters",
    PRODUCTS = "products",
}

interface ItemRowProps {
    style: React.CSSProperties;
    image?: string;
    title: string;
    titleAttr?: string;
    isExpanded: boolean;
    isSelected: boolean;
    onToggle: () => void;
    showFallbackIcon?: boolean;
    imageStyle?: React.CSSProperties;
    metaRows: React.ReactNode[];
}

interface VirtualizedListSectionProps {
    items: any[];
    containerRef: React.RefObject<HTMLDivElement | null>;
    containerClassName: string;
    itemSize: number;
    renderRow: (props: { index: number; style: React.CSSProperties }) => React.ReactElement;
    emptyText: string;
    showCreateButton?: boolean;
}

const LeftPanel: React.FC = memo(() => {
    const [containerHeight, setContainerHeight] = useState(400);
    const refs = {
        clusters: useRef<HTMLDivElement>(null),
        products: useRef<HTMLDivElement>(null)
    };
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);

    const {
        setIsModalVisible,
        setModalType,
        activeTab,
        setActiveTab,
        searchValue,
        handleSearch,
        handleCreateNew,
        filteredClusters,
        filteredProducts,
        expandedClusters,
        selectedClusters,
        handleClusterSelection,
        selectedProductIds,
        handleProductSelectionInLeftPanel: handleProductSelection,
        isLoadingProducts,
        productGroups,
    } = useContext(campaignCreationContext);

    const categoryOptions = useMemo(() => {
        return getCategoryOptions(productGroups || []);
    }, [productGroups]);

    const subcategoryOptions = useMemo(() => {
        if (!productGroups || !Array.isArray(productGroups)) return [];

        // If categories are selected, restrict subcategories to those belonging to the selected categories.
        let dataToUse = productGroups as IProductGroup[];

        if (selectedCategories.length > 0) {
            const selectedCategorySet = new Set(selectedCategories);
            dataToUse = dataToUse.filter((item) => selectedCategorySet.has(item.category));
        }

        const uniqueSubcategories = [
            ...new Set(
                dataToUse
                    .map((item) => item.subcategory)
                    .filter(
                        (subcat) =>
                            subcat && subcat !== "-" && subcat.trim() !== ""
                    )
            ),
        ];

        return uniqueSubcategories.map((subcat) => ({
            label: subcat,
            value: subcat,
        }));
    }, [productGroups, selectedCategories]);

    const isFilterActive =
        selectedCategories.length > 0 || selectedSubcategories.length > 0;

    const filterCount = selectedCategories.length;

    const categoryFilteredProducts = useMemo(() => {
        let filtered = filteredProducts;

        if (selectedCategories.length > 0) {
            const categorySet = new Set(selectedCategories);
            filtered = filtered.filter(
                (product: IProductGroup) => categorySet.has(product.category)
            );
        }

        if (selectedSubcategories.length > 0) {
            const subcategorySet = new Set(selectedSubcategories);
            filtered = filtered.filter((product: IProductGroup) =>
                subcategorySet.has(product.subcategory)
            );
        }

        return filtered;
    }, [filteredProducts, selectedCategories, selectedSubcategories]);

    const categoryFilteredClusters = useMemo(() => {
        if (selectedCategories.length === 0 && selectedSubcategories.length === 0) {
            return filteredClusters;
        }

        return filteredClusters.filter((cluster: IClusterFormData) => {
            const products = cluster.products || [];
            return products.some((product) => {
                const categoryMatch =
                    selectedCategories.length === 0 ||
                    selectedCategories.includes(product.category);
                const subcategoryMatch =
                    selectedSubcategories.length === 0 ||
                    selectedSubcategories.includes(product.subcategory);
                return categoryMatch && subcategoryMatch;
            });
        });
    }, [filteredClusters, selectedCategories, selectedSubcategories]);

    const handleCategoryChange = (values: string[]) => {
        setSelectedCategories(values);

        // When categories change, remove subcategories that no longer belong
        setSelectedSubcategories((prevSubcategories) => {
            if (!productGroups || values.length === 0) return [];

            const allowedSubcategories = new Set(
                (productGroups as IProductGroup[])
                    .filter((product) => values.includes(product.category))
                    .map((product) => product.subcategory)
                    .filter(
                        (subcat) =>
                            subcat && subcat !== "-" && subcat.trim() !== ""
                    )
            );

            return prevSubcategories.filter((subcat) =>
                allowedSubcategories.has(subcat)
            );
        });
    };

    const handleSubcategoryChange = (values: string[]) => {
        setSelectedSubcategories(values);
    };

    const handleClearAllFilters = () => {
        setSelectedCategories([]);
        setSelectedSubcategories([]);
    };

    const filterContent = (
        <div className="filter-dropdown-content">
            <div className="filter-dropdown-header">
                <span className="filter-dropdown-title">Filter by</span>
                <Button 
                    type="link" 
                    className="filter-clear-btn"
                    onClick={handleClearAllFilters}
                    disabled={!isFilterActive}
                >
                    Clear All
                </Button>
            </div>
            <Divider size='small' />
            <div className="filter-dropdown-body">
                <Select
                    mode="multiple"
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    options={categoryOptions}
                    className="filter-select"
                    popupMatchSelectWidth={false}
                    placeholder="Select Categories"
                    maxTagCount={1}
                />
                <Select
                    mode="multiple"
                    value={selectedSubcategories}
                    onChange={handleSubcategoryChange}
                    options={subcategoryOptions}
                    className="filter-select"
                    popupMatchSelectWidth={false}
                    placeholder="Select Subcategories"
                    maxTagCount={1}
                />
            </div>
        </div>
    );

    useEffect(() => {
        const updateHeight = () => {
            const currentRef = refs[activeTab as keyof typeof refs].current;
            if (currentRef) {
                const rect = currentRef.getBoundingClientRect();
                setContainerHeight(rect.height);
            }
        };

        updateHeight();

        const handleResize = () => updateHeight();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [categoryFilteredClusters.length, categoryFilteredProducts.length, activeTab]);

    const ItemRow = ({
        style,
        image,
        title,
        titleAttr,
        isExpanded,
        isSelected,
        onToggle,
        showFallbackIcon = false,
        imageStyle,
        metaRows,
    }: ItemRowProps) => (
        <div style={style}>
            <div className={`cluster-item ${isExpanded ? "expanded" : ""} ${isSelected ? "selected" : ""}`}>
                <div className={image ? "product-thumb" : "cluster-icon"}>
                    {image ? (
                        <img src={image} alt={title} style={imageStyle} />
                    ) : showFallbackIcon ? (
                        <AppstoreOutlined style={{ fontSize: "32px" }} />
                    ) : null}
                </div>
                <div className="cluster-content">
                    <div className="cluster-header">
                        <Text className={`cluster-name ${isExpanded ? "expanded" : ""}`} title={titleAttr}>
                            {title}
                        </Text>
                        <Button
                            type="text"
                            icon={
                                isSelected ? (
                                    <MinusOutlined
                                        style={{ fontSize: "19px" }}
                                    />
                                ) : (
                                    <PlusOutlined
                                        style={{ fontSize: "19px" }}
                                    />
                                )
                            }
                            size="small"
                            className="expand-button"
                            onClick={onToggle}
                        />
                    </div>
                    {metaRows.map((row, index) => (
                        <div key={index} className="cluster-meta">
                            {row}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const ClusterRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const cluster: IClusterFormData = categoryFilteredClusters[index];
        const isExpanded = expandedClusters.includes(cluster.id);
        const isSelected = selectedClusters.includes(cluster.id);
        const image = cluster?.products?.[0]?.thumbnail;

        const metaRows = [
            <>
                <Text className="cluster-count">
                    Products: {cluster.products?.length || 0}
                </Text>
                {cluster.createdAt && (
                    <>
                        <Text className="meta-separator"> • </Text>
                        <Text className="cluster-date">
                            Created on:{" "}
                            {new Date(cluster.createdAt).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            })}
                        </Text>
                    </>
                )}
            </>
        ];

        return (
            <ItemRow
                style={style}
                image={image}
                title={capitalizeFirst(cluster.name) || `Cluster ${cluster.id}`}
                isExpanded={isExpanded}
                isSelected={isSelected}
                onToggle={() => handleClusterSelection(cluster.id)}
                showFallbackIcon={!image}
                imageStyle={{ width: "56px", height: "46px" }}
                metaRows={metaRows}
            />
        );
    };

    const ProductRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const product: IProductGroup = categoryFilteredProducts[index];
        const isExpanded = !!product.expanded;
        const isSelected = selectedProductIds.includes(product.id);

        const metaRows = [
            <>
                <Text className="cluster-count">{startCase(product.category)}</Text>
                <Text className="meta-separator">•</Text>
                <Text className="cluster-date">{startCase(product.subcategory)}</Text>
            </>,
            <>
                <Text className="cluster-count">MS%: {product.msPercent}%</Text>
                <Text className="meta-separator">•</Text>
                <Text className="cluster-count">Org. SOV: {product.organic_sov}%</Text>
                <Text className="meta-separator">•</Text>
                <Text className="cluster-count">Paid SOV: {product.paid_sov}%</Text>
                <Text className="meta-separator">•</Text>
                <Text className="cluster-count">Wt Availability: {product.wtAvailabilityPercent}%</Text>
            </>
        ];

        return (
            <ItemRow
                style={style}
                image={product.image}
                title={product.title}
                titleAttr={product.title}
                isExpanded={isExpanded}
                isSelected={isSelected}
                onToggle={() => handleProductSelection(product.id)}
                metaRows={metaRows}
            />
        );
    };

    const SpinnerComponent = ({ isLoading }: { isLoading: boolean }) => {
        return (
            <Spin spinning={isLoading} size="large">
            </Spin>
        );
    };

    const VirtualizedListSection = ({
        items,
        containerRef,
        containerClassName,
        itemSize,
        renderRow,
        emptyText,
        showCreateButton = false,
    }: VirtualizedListSectionProps) => {
        if (items.length === 0) {
            return (
                <Empty
                    description={
                        <div className="empty-state">
                            {isLoadingProducts ? (
                                <SpinnerComponent isLoading={isLoadingProducts} />
                            ) : (
                                <>
                                    <Text type="secondary" className="empty-text">
                                        {emptyText}
                                    </Text>
                                    {showCreateButton && (
                                        <Button
                                            type="link"
                                            onClick={handleCreateNew}
                                            className="create-link text-decoration-none"
                                        >
                                            + Create New
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    }
                    className="empty-container"
                />
            );
        }

        return (
            <div ref={containerRef} className={containerClassName}>
                <List
                    height={containerHeight}
                    itemCount={items.length}
                    itemSize={itemSize}
                    width="100%"
                >
                    {renderRow}
                </List>
            </div>
        );
    };

    return (
        <div className="left-panel">
            <div
                className="panel-header"
                style={{ padding: "16px 24px", background: "#fff" }}
            >
                <div className="header-content">
                    <Text strong className="panel-title">
                        {activeTab === LEFT_PANEL_TABS.CLUSTERS
                            ? "Select Clusters"
                            : "Select Products"}
                    </Text>
                    <Button
                        type="primary"
                        className="add-button"
                        size="small"
                        onClick={() => {
                            setModalType(BCC_MODAL_TYPES.CLUSTER);
                            setIsModalVisible(true);
                        }}
                    >
                        <PlusOutlined />
                    </Button>
                </div>
            </div>

            <div className="button-container">
                <Button
                    type={activeTab === LEFT_PANEL_TABS.CLUSTERS ? "primary" : "default"}
                    onClick={() => setActiveTab(LEFT_PANEL_TABS.CLUSTERS)}
                    className={
                        activeTab === LEFT_PANEL_TABS.CLUSTERS
                            ? "active-button"
                            : "inactive-button"
                    }
                >
                    Clusters
                </Button>
                <Button
                    type={activeTab === LEFT_PANEL_TABS.PRODUCTS ? "primary" : "default"}
                    onClick={() => setActiveTab(LEFT_PANEL_TABS.PRODUCTS)}
                    className={
                        activeTab === LEFT_PANEL_TABS.PRODUCTS
                            ? "active-button"
                            : "inactive-button"
                    }
                >
                    Products
                </Button>
            </div>

            <div className="search-container">
                <div className="search-input-wrapper">
                    <Input
                        placeholder="Search"
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="search-input ps-3"
                        suffix={<LuSearch style={{ fontSize: "18px" }} />}
                    />
                </div>
                <Popover
                    content={filterContent}
                    trigger="click"
                    open={filterPopoverOpen}
                    onOpenChange={setFilterPopoverOpen}
                    placement="bottomRight"
                    overlayClassName="filter-dropdown-popover"
                >
                    <Button
                        type="default"
                        icon={
                            <Badge 
                                count={isFilterActive ? filterCount : 0} 
                                overflowCount={99}
                                offset={[7, -7]}
                                color="#0078aa"
                            >
                                <FilterOutlined />
                            </Badge>
                        }
                        className={`filter-button ${isFilterActive ? 'filter-active' : ''}`}
                    />
                </Popover>
            </div>

            <div className="content-area">
                {activeTab === LEFT_PANEL_TABS.CLUSTERS ? (
                    <VirtualizedListSection
                        items={categoryFilteredClusters}
                        containerRef={refs.clusters}
                        containerClassName="cluster-list-container"
                        itemSize={75}
                        renderRow={ClusterRow}
                        emptyText="No Cluster Found"
                        showCreateButton
                    />
                ) : (
                    <VirtualizedListSection
                        items={categoryFilteredProducts}
                        containerRef={refs.products}
                        containerClassName="cluster-list-container product-list-container"
                        itemSize={100}
                        renderRow={ProductRow}
                        emptyText="No Product Found"
                    />
                )}
            </div>
        </div>
    );
});

export default LeftPanel